<template>
	<BaseModal
		v-model:visible="ss.errorModalVisible"
		@hide="close"
		:header="current.header"
		:show-cancel-button="false"
		:show-ok-button="false"
		:use-gradient-background="true"
		:dismissable-mask="false"
	>
		<template #content>
			<div class="flex items-center gap-4">
				<GlyphIcon :icon="GlyphIconName['triangle-empty-alert']" size="64" color="var(--p-primary-500)" />
				<div class="message">{{ current.message }}</div>
			</div>
		</template>
		<template #footer="{ close: closeCallback }">
			<Button @click="closeCallback" class="w-36" size="small"><span>{{ t('common.ok') }}</span></Button>
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { ref, watch } from 'vue'
import type { ErrorModalMessage, ErrorModalMessageQueue } from '../../definitions/Common'
import globalEvents from '@/utilities/GlobalEvents'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { useI18n } from 'vue-i18n'
import BaseModal from './BaseModal.vue'

const ss = useSettingsStore()
const { t } = useI18n()
const queue = ref<ErrorModalMessageQueue>([])
const current = ref<ErrorModalMessage>({ header: '', message: '' })
let shownAt = 0

watch(() => ss.errorModalVisible, (value: boolean) => {
	if (value) {
		shownAt = Date.now()
		window.addEventListener('keydown', keyDownHandler)
	} else {
		window.removeEventListener('keydown', keyDownHandler)
	}
})

function keyDownHandler(event: KeyboardEvent) {
	// Ignore keydowns that arrive right after the modal opened - a held Enter
	// key (auto-repeat) from the action that triggered the modal would otherwise
	// dismiss it immediately.
	if (Date.now() - shownAt < 500) { return }
	if (event.key === 'Enter') {
		ss.errorModalVisible = false
	}
}

function show(data: ErrorModalMessage) {
	current.value = data
	ss.errorModalVisible = true
}

function queueOrShow(data: ErrorModalMessage) {
	if (!data) { return }
	if (!ss.errorModalVisible) {
		show(data)
	} else {
		if (current.value && current.value.header === data.header && current.value.message === data.message) { return } // Same message as currently showing - drop it
		if (queue.value.find(queued => { return queued.header === data.header && queued.message === data.message })) { return } // Same message is in queue - drop it
		queue.value.push(data)
	}
}

function close() {
	if (queue.value.length > 0) {
		setTimeout(() => {
			current.value = queue.value.shift() as ErrorModalMessage
			ss.errorModalVisible = true
		}, 200)
	}
}

globalEvents.on('CommonErrorModal', queueOrShow)

globalEvents.on('Wakeup', () => {
	queue.value = []
	ss.errorModalVisible = false
})

</script>

<style scoped>
.message {
	color: var(--p-primary-800);
}
@media (prefers-color-scheme: dark) {
	.message {
		color: var(--p-primary-300);
	}
}
</style>
